import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../sectepe-ui/index';
import salesCampaignService, { SalesCampaign } from '../services/salesCampaignService';
import gtmService, { Icp } from '../services/gtmService';
import { PageHeader, EmptyState, LoadingState, StatusBadge, useConfirm, StatusTone } from '../components/ui';
import { Icon } from '../components/icons';
import { SalesProcessBar } from '../vertrieb/SalesProcessBar';
import { KpiRow } from '../vertrieb/KpiRow';
import { datum, fehlerText } from '../vertrieb/format';
import { RechteHinweis } from '../vertrieb/RechteHinweis';
import { gtmPfad } from '../vertrieb/gtmLinks';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';

/**
 * Zielfirmen finden: eine Kampagne beschreibt Region, Branche und Kriterien,
 * die KI-Recherche liefert daraus Firmenkontakte. Qualifizierte Kontakte
 * wandern von der Detailseite als Firma in die Akte.
 *
 * Welle 2: Anlegen und Recherche verlangen vertrieb.bearbeiten, Loeschen
 * vertrieb.loeschen; der CSV-Export ist ein GET und bleibt fuer Lesende.
 */

export const KAMPAGNEN_STATUS: Record<string, { label: string; tone: StatusTone }> = {
  draft: { label: 'Entwurf', tone: 'neutral' },
  researching: { label: 'Recherche läuft', tone: 'info' },
  completed: { label: 'Abgeschlossen', tone: 'success' },
  failed: { label: 'Fehlgeschlagen', tone: 'danger' },
};

export const kampagnenStatus = (status: string) => KAMPAGNEN_STATUS[status] ?? { label: status, tone: 'neutral' as StatusTone };

const LEERES_FORMULAR = {
  name: '',
  description: '',
  targetRegion: 'Deutschland',
  targetIndustries: '',
  nearLocations: '',
  specificLocations: '',
  additionalCriteria: '',
  /** Optionales ICP aus dem GTM-Setup (schaerft die KI-Recherche). */
  icpId: '',
};

const liste = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

const SalesCampaignsPage: React.FC = () => {
  const { push } = useToast();
  const { confirm } = useConfirm();
  const { darf } = useBerechtigung();
  const darfBearbeiten = darf('vertrieb.bearbeiten');
  const darfLoeschen = darf('vertrieb.loeschen');
  const [kampagnen, setKampagnen] = useState<SalesCampaign[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [zeigeNeu, setZeigeNeu] = useState(false);
  const [formular, setFormular] = useState(LEERES_FORMULAR);
  const [speichert, setSpeichert] = useState(false);
  const [icps, setIcps] = useState<Icp[]>([]);

  const laden = useCallback(async () => {
    try {
      setKampagnen((await salesCampaignService.getCampaigns()) ?? []);
      setFehler(null);
    } catch (e) {
      setFehler(fehlerText(e, 'Kampagnen konnten nicht geladen werden'));
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  useEffect(() => {
    gtmService.getIcps().then((daten) => setIcps(daten ?? [])).catch(() => {
      // ICP-Grounding ist optional; ohne GTM-Setup bleibt die Auswahl leer.
    });
  }, []);

  const anlegen = async (e: React.FormEvent) => {
    e.preventDefault();
    setSpeichert(true);
    try {
      const branchen = liste(formular.targetIndustries);
      const orte = liste(formular.specificLocations);
      await salesCampaignService.createCampaign({
        name: formular.name.trim(),
        description: formular.description.trim() || undefined,
        targetRegion: formular.targetRegion.trim(),
        targetIndustries: branchen.length ? branchen : undefined,
        targetCriteria: {
          nearLocations: formular.nearLocations.trim() || undefined,
          specificLocations: orte.length ? orte : undefined,
          additionalCriteria: formular.additionalCriteria.trim() || undefined,
        },
        icpId: formular.icpId || undefined,
      });
      setFormular(LEERES_FORMULAR);
      setZeigeNeu(false);
      push({ message: `Kampagne „${formular.name.trim()}“ angelegt.`, severity: 'success' });
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Kampagne konnte nicht angelegt werden'));
    } finally {
      setSpeichert(false);
    }
  };

  const loeschen = async (k: SalesCampaign) => {
    const ok = await confirm({
      title: 'Kampagne löschen?',
      message: `„${k.name}“ und alle recherchierten Kontakte werden entfernt. Bereits angelegte Firmen bleiben in der Akte.`,
      confirmLabel: 'Löschen',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await salesCampaignService.deleteCampaign(k.id);
      push({ message: `Kampagne „${k.name}“ gelöscht.`, severity: 'info' });
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Löschen fehlgeschlagen'));
    }
  };

  const recherchieren = async (k: SalesCampaign) => {
    const ok = await confirm({
      title: 'KI-Recherche starten?',
      message: `Die Recherche sucht Firmen für „${k.name}“ in ${k.targetRegion}. Das dauert je nach Kriterien einige Minuten und verbraucht KI-Kontingent.`,
      confirmLabel: 'Recherche starten',
    });
    if (!ok) return;
    setFehler(null);
    setKampagnen((alt) => alt.map((c) => (c.id === k.id ? { ...c, status: 'researching' } : c)));
    try {
      const ergebnis = await salesCampaignService.researchContacts(k.id);
      push({ message: `Recherche abgeschlossen: ${ergebnis.contactsCreated} Kontakte gefunden.`, severity: 'success', duration: 8000 });
    } catch (e) {
      setFehler(fehlerText(e, 'Recherche fehlgeschlagen'));
    } finally {
      await laden();
    }
  };

  const exportieren = async (k: SalesCampaign) => {
    try {
      const csv = await salesCampaignService.exportContacts(k.id, 'csv');
      salesCampaignService.downloadCSV(csv, `kampagne-${k.id}.csv`);
    } catch (e) {
      setFehler(fehlerText(e, 'Export fehlgeschlagen'));
    }
  };

  const zaehle = (st: string) => kampagnen.filter((k) => k.status === st).length;
  const kontakte = kampagnen.reduce((s, k) => s + (k._count?.contacts ?? 0), 0);
  const feld = (name: keyof typeof LEERES_FORMULAR) => ({
    value: formular[name],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormular({ ...formular, [name]: e.target.value }),
  });

  return (
    <div className="page-container">
      <SalesProcessBar stepId="zielfirmen" />
      <PageHeader
        title="Zielfirmen recherchieren (KI)"
        hilfe="kampagnen"
        subtitle="Region, Branche und Kriterien festlegen – die KI-Recherche liefert daraus Firmen mit Ansprechpartnern. Qualifizierte Treffer werden zur Firma in der Akte."
        actions={darfBearbeiten ? (
          <button type="button" className="btn btn-primary" onClick={() => setZeigeNeu((v) => !v)}>
            <Icon name="plus" size={14} /> Neue Kampagne
          </button>
        ) : undefined}
      />
      <RechteHinweis was="Kampagnen anlegen und Recherche starten" />

      {!laedt && kampagnen.length > 0 && (
        <KpiRow
          aria-label="Kampagnen-Kennzahlen"
          items={[
            { label: 'Kampagnen', value: kampagnen.length },
            { label: 'Entwürfe', value: zaehle('draft'), hint: zaehle('draft') ? 'Recherche noch zu starten' : undefined },
            { label: 'in Recherche', value: zaehle('researching'), tone: zaehle('researching') ? 'warn' : 'neutral' },
            { label: 'Kontakte gefunden', value: kontakte, tone: kontakte ? 'good' : 'neutral' },
          ]}
        />
      )}

      {fehler && <div className="alert alert-error">{fehler}</div>}

      {zeigeNeu && darfBearbeiten && (
        <form className="card kampagne-formular" onSubmit={anlegen} aria-label="Neue Kampagne">
          <h3 className="card-title">Neue Vertriebskampagne</h3>
          <div className="akte-felder">
            <label>
              Kampagnenname *
              <input type="text" required autoFocus placeholder="z. B. Zulieferer Süddeutschland 2026" {...feld('name')} />
            </label>
            <label>
              Zielregion *
              <input type="text" required placeholder="z. B. Deutschland, Baden-Württemberg" {...feld('targetRegion')} />
            </label>
            <label>
              Zielbranchen (Komma-getrennt)
              <input type="text" placeholder="z. B. Catering, Logistik, Facility Management" {...feld('targetIndustries')} />
            </label>
            <label>
              In der Nähe von
              <input type="text" placeholder="z. B. Bundeswehr-Standorte" {...feld('nearLocations')} />
            </label>
            <label>
              Konkrete Standorte (Komma-getrennt)
              <input type="text" placeholder="z. B. Ramstein, Stuttgart, München" {...feld('specificLocations')} />
            </label>
          </div>
          <label className="kampagne-formular__breit">
            ICP-Grounding (optional)
            <select
              className="form-select"
              value={formular.icpId}
              onChange={(e) => setFormular({ ...formular, icpId: e.target.value })}
            >
              <option value="">– kein ICP –</option>
              {icps.map((i) => (
                <option key={i.id} value={i.id}>{i.name}</option>
              ))}
            </select>
            <span className="text-xs text-muted">
              Ein ICP aus dem <Link to="/gtm-setup">GTM-Setup</Link> schärft die KI-Recherche (Branche, Signale, Pain Points).
            </span>
          </label>
          <label className="kampagne-formular__breit">
            Beschreibung
            <textarea className="form-textarea" rows={2} placeholder="Wofür ist die Kampagne gedacht?" {...feld('description')} />
          </label>
          <label className="kampagne-formular__breit">
            Zusätzliche Kriterien
            <textarea className="form-textarea" rows={2} placeholder="Weitere Anforderungen oder Ausschlüsse" {...feld('additionalCriteria')} />
          </label>
          <div className="btn-group">
            <button type="submit" className="btn btn-primary" disabled={speichert || !formular.name.trim()}>
              {speichert ? 'Legt an…' : 'Kampagne anlegen'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setZeigeNeu(false)}>Abbrechen</button>
          </div>
        </form>
      )}

      {laedt ? (
        <LoadingState rows={3} label="Kampagnen werden geladen" />
      ) : kampagnen.length === 0 ? (
        <EmptyState
          icon="megaphone"
          title="Noch keine Kampagne"
          description="Beschreibe, welche Firmen du suchst: Region, Branche, Nähe zu Standorten. Die Recherche liefert dann eine Liste mit Ansprechpartnern, die du qualifizierst und in die Akte übernimmst."
          action={darfBearbeiten ? { label: 'Erste Kampagne anlegen', onClick: () => setZeigeNeu(true) } : undefined}
          secondaryAction={{ label: 'Direkt zu den Leads', to: '/lead-sync' }}
        />
      ) : (
        <div className="grid-cards">
          {kampagnen.map((k) => {
            const st = kampagnenStatus(k.status);
            const anzahl = k._count?.contacts ?? 0;
            return (
              <div key={k.id} className="card kampagne-karte">
                <div className="card-header">
                  <div className="kampagne-karte__kopf">
                    <h3 className="card-title">
                      <Link to={`/sales-campaigns/${k.id}`}>{k.name}</Link>
                    </h3>
                    <StatusBadge tone={st.tone}>{st.label}</StatusBadge>
                  </div>
                  {k.description && <p className="card-description">{k.description}</p>}
                  <dl className="sales-facts">
                    {k.icpId && (
                      <div>
                        <dt>Zielkundenprofil</dt>
                        <dd><Link to={gtmPfad('icp', k.icpId)} title="Zielkundenprofil im GTM-Setup öffnen">{icps.find((i) => i.id === k.icpId)?.name ?? 'öffnen'}</Link></dd>
                      </div>
                    )}
                    <div><dt>Region</dt><dd>{k.targetRegion}</dd></div>
                    {(k.targetIndustries?.length ?? 0) > 0 && <div><dt>Branchen</dt><dd>{k.targetIndustries!.join(', ')}</dd></div>}
                    <div><dt>Kontakte</dt><dd>{anzahl}</dd></div>
                    <div><dt>Angelegt</dt><dd>{datum(k.createdAt)}</dd></div>
                  </dl>
                </div>
                <div className="card-footer">
                  <Link to={`/sales-campaigns/${k.id}`} className="btn btn-secondary btn-sm">
                    Kontakte <Icon name="arrow-right" size={14} />
                  </Link>
                  {darfBearbeiten && k.status === 'draft' && (
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => recherchieren(k)}>
                      <Icon name="zap" size={14} /> Recherche starten
                    </button>
                  )}
                  {darfBearbeiten && k.status === 'failed' && (
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => recherchieren(k)}>
                      <Icon name="refresh" size={14} /> Erneut versuchen
                    </button>
                  )}
                  {k.status === 'completed' && anzahl > 0 && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => exportieren(k)}>CSV</button>
                  )}
                  {darfLoeschen && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => loeschen(k)} aria-label={`${k.name} löschen`}>
                      <Icon name="trash" size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SalesCampaignsPage;
